import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { CirclePlus, Delete, DeleteIcon, Edit } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import axios from "axios";
import { toast, Toaster } from "sonner";

const Spinner = () => (
  <div className="inline-block w-5 h-5 border-4 border-t-transparent border-gray-600 rounded-full animate-spin"></div>
);
const Offers = () => {
  const token = localStorage.getItem("adminToken");
  const [offers, setOffers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOffers = async () => {
    try {
      setIsLoading(true)
      const res = await axios.get(
        "https://wavetechservices.in/api/admin/services/offers",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setOffers(res.data.data);
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching offers:", error);
      toast.error("Failed to fetch offers");
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const [addFormData, setAddFormData] = useState({
    name: "",
    couponId: "",
    discount: "",
    image: null,
    startDate: "",
    endDate: "",
  });

  const [updateFormData, setUpdateFormData] = useState({
    name: "",
    couponId: "",
    discount: "",
    image: null,
    startDate: "",
    endDate: "",
  });

  const handleChange = (e) => {
    const { id, value, type, files } = e.target;
    if (type === "file") {
      setAddFormData((prev) => ({
        ...prev,
        [id]: files[0],
      }));
    } else {
      setAddFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const handleUpdateChange = (e) => {
    const { id, value, type, files } = e.target;
    if (type === "file") {
      setUpdateFormData((prev) => ({
        ...prev,
        [id]: files[0],
      }));
    } else {
      setUpdateFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true)
    const formData = new FormData();
    formData.append("offerTitle", addFormData.name);
    formData.append("couponCode", addFormData.couponId);
    formData.append("discount", addFormData.discount);
    formData.append("startDate", addFormData.startDate);
    formData.append("endDate", addFormData.endDate);
    if (addFormData.image) {
      formData.append("promotionalImage", addFormData.image);
    }

    try {
      await axios.post(
        "https://wavetechservices.in/api/admin/services/offers",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Offer uploaded successfully!");
      setIsSubmitting(false)
      fetchOffers();
      setOpenAdd(false);
      setAddFormData({
        name: "",
        couponId: "",
        discount: "",
        image: null,
        startDate: "",
        endDate: "",
      });
    } catch (error) {
      console.error("Error uploading offer:", error);
      toast.error("Error while uploading!");
      setIsSubmitting(false)
    }
  };

  const handleUpdate = async (offer) => {
    setIsSubmitting(true)
    const formData = new FormData();
    formData.append("offerTitle", updateFormData.name);
    formData.append("couponCode", updateFormData.couponId);
    formData.append("discount", updateFormData.discount);
    formData.append("startDate", updateFormData.startDate);
    formData.append("endDate", updateFormData.endDate);
    if (updateFormData.image) {
      formData.append("promotionalImage", updateFormData.image);
    }
    try {
      await axios.put(
        `https://wavetechservices.in/api/admin/services/offers/${offer._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchOffers();
      setIsSubmitting(false)
      toast.success("Offer Updated successfully!");
      setOpenUpdate(false);
    } catch (error) {
      setIsSubmitting(false)
      console.error("Error updating offer:", error);
      toast.error("Error while updating!");
    }
  };

  const handleDelete = async (offer) => {
    try {
      setIsSubmitting(true)
      await axios.delete(
        `https://wavetechservices.in/api/admin/services/offers/${offer._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchOffers();
      setIsSubmitting(false)
      setOpenDelete(false);
      toast.success("Offer Deleted successfully");
    } catch (err) {
      console.error(err);
      setIsSubmitting(false)
      toast.error("Failed to delete offer");
    }
  };

  // Search functionality
  const filteredOffers = useMemo(() => {
    return offers.filter(offer =>
      offer.offerTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.couponCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      offer.discount.toString().includes(searchTerm)
    );
  }, [offers, searchTerm]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOffers = filteredOffers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOffers.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const maxVisiblePages = 5;
  const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="px-[1vw] my-5">
      <Toaster />
      <div className="flex lg:justify-between lg:items-center flex-col lg:flex-row">
        <h1 className="text-2xl">Offers - {filteredOffers.length}</h1>
        <div className="flex lg:gap-4 items-center gap-2 my-3 lg:my-0">
          <Input
            placeholder="Search offers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger>
              <div className="flex items-center">
                <Button className="bg-black px-5 text-white h-9 hover:opacity-90">
                  Add <CirclePlus className="size-2" />
                </Button>
              </div>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="lg:text-2xl text-xl">
                  Add Offers
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-col py-4 space-y-3">
                <div className="flex items-center gap-4">
                  <Label htmlFor="name" className="w-44">
                    Offer Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={addFormData.name}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="couponId" className="w-44">
                    Coupon ID
                  </Label>
                  <Input
                    id="couponId"
                    type="text"
                    value={addFormData.couponId}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="discount" className="w-44">
                    Discount %
                  </Label>
                  <Input
                    id="discount"
                    type="number"
                    value={addFormData.discount}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="image" className="w-44">
                    Image
                  </Label>
                  <Input
                    id="image"
                    type="file"
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="startDate" className="w-44">
                    Start Date
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={addFormData.startDate}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <Label htmlFor="endDate" className="w-44">
                    End Date
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={addFormData.endDate}
                    onChange={handleChange}
                    className="col-span-3"
                  />
                </div>
              </div>
              <div className="flex justify-center items-center">
                <Button
                  type="submit"
                  className="bg-black px-5 text-white h-9 hover:opacity-90 flex items-center justify-center gap-2"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner />
                      Adding...
                    </>
                  ) : (
                    <> Add <CirclePlus className="size-2" /></>
                  )}

                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Separator className="my-4" />
      {isLoading ? (<TableRow className="flex justify-center">
        <TableCell colSpan={6} className="text-center py-8">
          <div className="flex items-center justify-center gap-2">
            <Spinner />
            Loading offer...
          </div>
        </TableCell>
      </TableRow>) : (<div className="overflow-x-auto lg:max-w-[1000px] max-w-[380px] md:max-w-[580px] lg:h-[65vh] h-[58vh] md:h-[70vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sl No.</TableHead>
              <TableHead className="w-[300px]">Offers</TableHead>
              <TableHead>Coupon ID</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead className="truncate">Start Date</TableHead>
              <TableHead className="truncate">End Date</TableHead>
              <TableHead>Image</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentOffers.length > 0 ? (
              currentOffers.map((offer, ind) => (
                <TableRow key={offer._id}>
                  <TableCell>{indexOfFirstItem + ind + 1}</TableCell>
                  <TableCell className="font-medium">
                    {offer.offerTitle}
                  </TableCell>
                  <TableCell>{offer.couponCode}</TableCell>
                  <TableCell>{offer.discount} %</TableCell>
                  <TableCell>
                    {new Date(offer.startDate).toISOString().split("T")[0]}
                  </TableCell>
                  <TableCell>
                    {new Date(offer.endDate).toISOString().split("T")[0]}
                  </TableCell>
                  <TableCell>
                    <img
                      src={`${offer.promotionalImage}`}
                      alt="Offer"
                      className="w-8"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-4 items-center">
                      <Dialog open={openUpdate} onOpenChange={setOpenUpdate}>
                        <DialogTrigger onClick={() => {
                          setUpdateFormData({
                            name: offer.offerTitle,
                            couponId: offer.couponCode,
                            discount: offer.discount,
                            image: null,
                            startDate: new Date(offer.startDate).toISOString().split("T")[0],
                            endDate: new Date(offer.endDate).toISOString().split("T")[0],
                          });
                        }}>
                          <Edit className="text-blue-500 size-5 cursor-pointer" />
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                          <DialogHeader>
                            <DialogTitle className="lg:text-2xl text-xl">
                              Edit Offers
                            </DialogTitle>
                          </DialogHeader>
                          <div className="flex flex-col py-4 space-y-3">
                            <div className="flex items-center gap-4">
                              <Label htmlFor="name" className="w-44">
                                Offer Name
                              </Label>
                              <Input
                                id="name"
                                type="text"
                                value={updateFormData.name}
                                onChange={handleUpdateChange}
                                className="col-span-3"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="couponId" className="w-44">
                                Coupon ID
                              </Label>
                              <Input
                                id="couponId"
                                type="text"
                                value={updateFormData.couponId}
                                onChange={handleUpdateChange}
                                className="col-span-3"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="discount" className="w-44">
                                Discount %
                              </Label>
                              <Input
                                id="discount"
                                type="number"
                                value={updateFormData.discount}
                                onChange={handleUpdateChange}
                                className="col-span-3"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="image" className="w-44">
                                Image
                              </Label>
                              <Input
                                id="image"
                                type="file"
                                onChange={handleUpdateChange}
                                className="col-span-3"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="startDate" className="w-44">
                                Start Date
                              </Label>
                              <Input
                                id="startDate"
                                type="date"
                                value={updateFormData.startDate}
                                onChange={handleUpdateChange}
                                className="col-span-3"
                              />
                            </div>
                            <div className="flex items-center gap-4">
                              <Label htmlFor="endDate" className="w-44">
                                End Date
                              </Label>
                              <Input
                                id="endDate"
                                type="date"
                                value={updateFormData.endDate}
                                onChange={handleUpdateChange}
                                className="col-span-3"
                              />
                            </div>
                          </div>
                          <div className="flex justify-center items-center">
                            <Button
                              type="submit"
                              className="bg-black px-5 text-white h-9 hover:opacity-90 flex items-center justify-center gap-2"
                              onClick={() => handleUpdate(offer)}
                              disabled={isSubmitting}
                            >
                              {
                                isSubmitting ? (<>
                                <Spinner/>
                                Saving...
                                </>):"Save"
                              }
                              
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
                        <DialogTrigger>
                          <DeleteIcon className="text-red-500 size-6 cursor-pointer" />
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Are you absolutely sure?</DialogTitle>
                            <DialogDescription>
                              This action cannot be undone. Are you sure you
                              want to permanently delete this offer?
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button
                              type="submit"
                              className="flex items-center justify-center gap-2"
                              onClick={() => handleDelete(offer)}
                              disabled={isSubmitting}
                            >
                              {
                                isSubmitting ? (<>
                                <Spinner/>
                                Deleting...
                                </>):"Confirm"
                              }
                              
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  {searchTerm ? "No matching offers found" : "No offers available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>)}


      {filteredOffers.length > 0 && (
        <div>
          <Pagination className="mt-4">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
              </PaginationItem>

              {startPage > 1 && (
                <>
                  <PaginationItem>
                    <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
                  </PaginationItem>
                  {startPage > 2 && (
                    <PaginationItem>
                      <span className="px-2">...</span>
                    </PaginationItem>
                  )}
                </>
              )}

              {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={page === currentPage}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}

              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && (
                    <PaginationItem>
                      <span className="px-2">...</span>
                    </PaginationItem>
                  )}
                  <PaginationItem>
                    <PaginationLink onClick={() => handlePageChange(totalPages)}>
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                </>
              )}

              <PaginationItem>
                <PaginationNext
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default Offers;